import type { ErrorEvent, EventHint } from "@sentry/browser";

export const SENTRY_IGNORE_ERRORS = [
  // Firebase unsupported errors
  'indexed-db-unsupported',
  'messaging/unsupported-browser',
  // OrbitControls errors
  '.touches[1]',
  "(reading 'pageX')",
  // dynamic modules load errors
  'Failed to fetch dynamically imported module',
  'Unable to preload CSS',
  "'text/html' is not a valid JavaScript MIME type",
  'Importing a module script failed',
  'The operation is insecure',
  // monetag ad errors
  "Right-hand side of 'instanceof' is not callable",
  // Plugin is not a function. (evaluating 'e[r]instanceof Plugin')
  // occurs when we remove the monetag interstitial script
  "]instanceof Plugin')",
  'Plugin is not a function',
]

function test(target: string, code: string | RegExp) {
  return typeof code === 'string'
    ? target.includes(code)
    : code.test(target);
}

export function isSentryIgnored(error: string) {
  return SENTRY_IGNORE_ERRORS.some(code => test(error, code))
}

// Firestore SDK internal assertion, e.g.
// "FIRESTORE (11.8.0) INTERNAL ASSERTION FAILED: Unexpected state (ID: b815) CONTEXT: {...(ID: ca9)...}"
const FIRESTORE_ASSERTION = /FIRESTORE \([^)]*\) INTERNAL ASSERTION FAILED/
const FIRESTORE_ASSERTION_ID = /\(ID: ([0-9a-f]+)\)/gi
let firestoreAssertionReported = false

/**
 * Once a Firestore assertion fails, the SDK's async queue is permanently failed and
 * every later Firestore operation (including the window 'online' event) throws a
 * wrapping "b815" error that contains the original one. Report only the first
 * occurrence per page session, and group all of them into a single Sentry issue
 * keyed by the root assertion ID (the innermost one), regardless of SDK version or
 * bundle hash.
 *
 * @returns false if the event should be dropped
 */
function handleFirestoreAssertion(event: ErrorEvent, text: string) {
  if (!FIRESTORE_ASSERTION.test(text)) return true
  if (firestoreAssertionReported) return false
  firestoreAssertionReported = true

  const ids = [...text.matchAll(FIRESTORE_ASSERTION_ID)].map(m => m[1])
  const rootId = ids[ids.length - 1] ?? 'unknown'
  event.fingerprint = ['firestore-internal-assertion', rootId]
  event.tags = { ...event.tags, firestore_assertion: rootId }
  return true
}

export function sentryBeforeSend(event: ErrorEvent, hint: EventHint) {
  const msg = event.message ?? ''

  // check message string
  if (isSentryIgnored(msg)) return null

  // check exception values
  const exTexts: string[] = []
  for (const ex of event.exception?.values ?? []) {
    const val = `${ex.type ?? ''}: ${ex.value ?? ''}`
    if (isSentryIgnored(val)) return null
    exTexts.push(val)
  }

  if (!handleFirestoreAssertion(event, [msg, ...exTexts].join('\n'))) return null

  // check originalException fields
  const orig = hint?.originalException as any
  if (typeof orig?.code === 'string' && isSentryIgnored(orig.code)) return null

  return event
}
